import { CobaltAvatar } from '@cae/cobalt-react/components/avatar';
import {
  CobaltGlobalHeader,
  CobaltUserMenu,
  CobaltUserMenuContent,
  CobaltUserMenuFooter,
  CobaltUserMenuTrigger,
} from '@cae/cobalt-react/components/global-header';
import { CobaltLink } from '@cae/cobalt-react/components/link';
import { MediaQuery } from '@cae/cobalt-react/enums';
import { useMediaQuery } from '@cae/cobalt-react/hooks';
import { CobaltIconLogInOutline } from '@cae/cobalt-react/icons/outline/CobaltIconLogInOutline';
import { CobaltIconUserAvatarOutline } from '@cae/cobalt-react/icons/outline/CobaltIconUserAvatarOutline';
import { CobaltIconQrCodeSolid } from '@cae/cobalt-react/icons/solid/CobaltIconQrCodeSolid';
import { useAuthenticatedUser } from '@contexts/auth-provider';
import { useHasPermission } from '@contexts/permissions-provider';
import { HelpButton } from '@features/help/components/help-button/HelpButton';
import { APP_ROUTES } from '@routes/routes.constants';
import { EXTERNAL_LINKS } from '@shared/constants/externalLinks';
import { useLogout } from '@shared/hooks/use-logout/useLogout';
import { useFlags } from 'launchdarkly-react-client-sdk';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import type { AppHeaderProps } from './AppHeader.types';

import styles from './AppHeader.module.css';

const AppHeader = React.memo(
({ hasSideNavigation }: Readonly<AppHeaderProps>): React.ReactElement => {
  const authData = useAuthenticatedUser();
  const isMobile = useMediaQuery(MediaQuery.Mobile);
  const logout = useLogout();
  const navigate = useNavigate();
  const { accessPageEnabledWebApp, hasThemeSwitcher } = useFlags();
  const isVisitor = useHasPermission('AccessPassNoTraining_View_Own');
  const isTrainee = useHasPermission('AccessPassWithTraining_View_Own');
  const canViewProfileTraining = useHasPermission(
    'ProfilePersonalTraining_FullAccess_Own'
  );
  const canViewProfileBasic = useHasPermission(
    'ProfilePersonalBasic_FullAccess_Own'
  );
  const canViewProfilePassword = useHasPermission(
    'ProfilePassword_FullAccess_Own'
  );
  const canViewProfileNotifications = useHasPermission(
    'ProfileNotifications_FullAccess_Own'
  );
  const canViewProfileLink =
    canViewProfileTraining ||
    canViewProfileBasic ||
    canViewProfilePassword ||
    canViewProfileNotifications;
  const canViewAccessPass = (isVisitor || isTrainee) && accessPageEnabledWebApp;

  const handleHeaderClick = React.useCallback(() => {
    navigate(APP_ROUTES.APP_INITIALIZER, { replace: true });
  }, [navigate]);

  const handleProfileClick = React.useCallback((): void => {
    navigate(APP_ROUTES.PROFILE);
  }, [navigate]);

  const handleAccessPassClick = React.useCallback((): void => {
    navigate(APP_ROUTES.ACCESS_PASS);
  }, [navigate]);

  return (
    <CobaltGlobalHeader
      headerTitle="Connect"
      data-testid="app-header"
      onHeaderTitleClick={handleHeaderClick}
    >
      {isMobile && !hasSideNavigation && (
        <button
          aria-label="CAE Connect home"
          className={`${styles.mobileHomeButton} heading-01`}
          data-testid="mobile-home-button"
          onClick={handleHeaderClick}
          type="button"
        >
          <span className={styles.mobileHomeLogo}>CAE</span>
          <span
            className={styles.mobileHomeLabel}
            data-testid="mobile-home-label"
          >
            Connect
          </span>
        </button>
      )}
      <HelpButton />
      <CobaltUserMenu
        userProfile={{
          firstName: authData.firstName,
          lastName: authData.lastName,
          email: authData.email,
        }}
        hasThemeSwitcher={hasThemeSwitcher}
      >
        <CobaltUserMenuTrigger>
          <CobaltAvatar
            firstName={authData.firstName}
            lastName={authData.lastName}
            variant="initials"
          />
        </CobaltUserMenuTrigger>
        <CobaltUserMenuContent>
          {canViewProfileLink && (
            <CobaltLink aria-label="Profile" onClick={handleProfileClick}>
              <CobaltIconUserAvatarOutline size="medium" />
              Profile
            </CobaltLink>
          )}
          {canViewAccessPass && (
            <CobaltLink
              aria-label="Access Pass"
              onClick={handleAccessPassClick}
            >
              <CobaltIconQrCodeSolid size="medium" />
              Access Pass
            </CobaltLink>
          )}
          <CobaltLink aria-label="Log out" onClick={logout}>
            <CobaltIconLogInOutline size="medium" />
            Log out
          </CobaltLink>
        </CobaltUserMenuContent>
        <CobaltUserMenuFooter>
          <CobaltLink aria-label="Terms of use" variant="secondary" size="x-small" href={EXTERNAL_LINKS.TERMS_OF_USE} target="_blank" rel="noopener noreferrer">
            Terms of use
          </CobaltLink>
          <span aria-hidden="true"> • </span>
          <CobaltLink aria-label="Privacy policy" variant="secondary" size="x-small" href={EXTERNAL_LINKS.PRIVACY_POLICY} target="_blank" rel="noopener noreferrer">
            Privacy policy
          </CobaltLink>
        </CobaltUserMenuFooter>
      </CobaltUserMenu>
    </CobaltGlobalHeader>
  );
}
);

AppHeader.displayName = 'AppHeader';

export { AppHeader };
